import React, { type MouseEvent } from 'react';
import { useI18nContext } from '@ecg-marktplaats/js-react-i18n';
import { Icon, Link, TextLarge, TextRegular, UtilsBlock } from '@hz-design-system/web-ui';

const safeReviewScore = (score: number) => {
  const MAX_VALID_VALUE = 5;
  const MIN_VALID_VALUE = 0;
  if (score > MAX_VALID_VALUE) return MAX_VALID_VALUE;
  if (score < MIN_VALID_VALUE) return MIN_VALID_VALUE;

  return score;
};

type ReviewsProps = {
  reviews: TReviewItem | null;
  reviewsLocationCount: number;
  onClickReviewLink: () => void;
};

const Reviews = ({ reviews, reviewsLocationCount, onClickReviewLink }: ReviewsProps) => {
  const { t } = useI18nContext();

  if (!reviews) {
    return;
  }

  const withReviewAttribution = reviews.reviewSystem === 'GOOGLE_REVIEWS';

  const handleClickReviewsLink = (evt: MouseEvent<HTMLButtonElement>) => {
    evt.preventDefault();
    onClickReviewLink();
  };

  if (reviewsLocationCount > 1) {
    return (
      <div className="reviews">
        <Icon name="saved" color="text-brand-primary" />
        <Link className="dialog-link" onClick={handleClickReviewsLink}>
          {t('soi.sellerHeader.multiLocationReview.link')}
        </Link>
        <TextLarge>{t('soi.sellerHeader.multiLocationReview.copy', { count: reviewsLocationCount })}</TextLarge>
      </div>
    );
  }

  return (
    <div className="reviews">
      <div
        role="group"
        aria-label={t('soi.sellerHeader.starRating', { score: safeReviewScore(reviews.rating).toFixed(1) })}
      >
        <Icon name="saved" color="text-brand-primary" />
        <TextLarge strong>{`${safeReviewScore(reviews.rating).toFixed(1)} · `}</TextLarge>
      </div>

      <Link className="dialog-link" onClick={handleClickReviewsLink} href="">
        {`${reviews.numberOfReviews} ${t('soi.sellerHeader.experiences')}`}
      </Link>

      {withReviewAttribution && (
        <UtilsBlock as="span" marginLeft="xxxs">
          <Icon name="info" color="text-secondary" className="u-marginRightXxxxs" />
          <TextRegular color="text-secondary">Google Maps</TextRegular>
        </UtilsBlock>
      )}
    </div>
  );
};

export default Reviews;
